import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, addDoc, query, where, getDocs, orderBy } from 'firebase/firestore';
import { auth } from '../firebase';
import { ChevronDown, ChevronUp } from 'lucide-react';

const db = getFirestore();

const PildorasSonneil = ({ setCurrentPage }) => {
  const [articleText, setArticleText] = useState('');
  const [articleLink, setArticleLink] = useState('');
  const [summary, setSummary] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [storedSummaries, setStoredSummaries] = useState([]);
  const [expandedSummary, setExpandedSummary] = useState(null);

  useEffect(() => {
    fetchStoredSummaries();
  }, []);

  const fetchStoredSummaries = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        // setError('User not authenticated. Cannot fetch history.');
        return;
      }
      const q = query(
        collection(db, "pildorasSummaries"),
        where("userId", "==", user.uid),
        orderBy("timestamp", "desc")
      );
      const querySnapshot = await getDocs(q);
      const summaries = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStoredSummaries(summaries);
    } catch (err) {
      console.error("Error fetching stored summaries:", err);
      setError('Could not fetch summary history.');
    }
  };

  const handleSummarize = async () => {
    if (!articleText.trim() || !articleLink.trim()) {
      setError('Please provide both article text and a link.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setSummary('');

    try {
      const functions = getFunctions();
      const summarizePildora = httpsCallable(functions, 'summarizePildora');
      const result = await summarizePildora({ article_text: articleText, article_link: articleLink });
      
      const newSummary = result.data.summary;
      setSummary(newSummary);

      try {
        const user = auth.currentUser;
        if (user) {
          await addDoc(collection(db, "pildorasSummaries"), {
            userId: user.uid,
            articleText,
            articleLink,
            summary: newSummary,
            timestamp: new Date(),
          });
          fetchStoredSummaries();
          setArticleText('');
          setArticleLink('');
        }
      } catch (firestoreError) {
        console.error("Error saving summary:", firestoreError);
        setError('Summary generated but failed to save to history.');
      }

    } catch (err) {
      console.error("Error calling summarizePildora function:", err);
      setError('An error occurred while summarizing the article. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSummary = (id) => {
    setExpandedSummary(expandedSummary === id ? null : id);
  };

  return (
    <div className="p-4 md:p-6">
      <button onClick={() => setCurrentPage('home')} className="bg-[rgb(100,204,201)] text-white py-2 px-4 rounded hover:bg-[rgb(80,184,181)] transition-colors mb-4">
        &larr; Back to Home
      </button>
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Resumen noticias Sonneil - Píldoras LinkedIn</h2>
        <p className="mb-4 text-gray-600">Este herramienta resume una noticia de propiedad inmobiliaria en un resumen conciso y atractivo para la marca Sonneil, siguiendo las pautas de estilo específicas.</p>
        
        <div className="mb-4">
          <label htmlFor="articleText" className="block text-gray-700 text-sm font-bold mb-2">
            Article Text
          </label>
          <textarea
            id="articleText"
            value={articleText}
            onChange={(e) => setArticleText(e.target.value)}
            placeholder="Pega el texto completo de la noticia aquí..."
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-48 resize-y"
          />
        </div>

        <div className="mb-6">
          <label htmlFor="articleLink" className="block text-gray-700 text-sm font-bold mb-2">
            Enlace de la noticia
          </label>
          <input
            id="articleLink"
            type="url"
            value={articleLink}
            onChange={(e) => setArticleLink(e.target.value)}
            placeholder="https://example.com/news-article"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>

        <button
          onClick={handleSummarize}
          disabled={isLoading}
          className="bg-[rgb(100,204,201)] text-white font-bold py-2 px-4 rounded hover:bg-[rgb(80,184,181)] focus:outline-none focus:shadow-outline disabled:bg-gray-400"
        >
          {isLoading ? 'Summarizing...' : 'Summarize'}
        </button>

        {error && <p className="text-red-500 text-xs italic mt-4">{error}</p>}

        {summary && (
          <div className="mt-6 p-4 bg-gray-50 rounded border border-gray-200">
            <h3 className="text-xl font-bold mb-2 text-gray-800">Summary</h3>
            <div className="whitespace-pre-wrap text-gray-700">{summary}</div>
          </div>
        )}

        <div className="mt-8">
          <h3 className="text-xl font-semibold mb-4 text-gray-800">History</h3>
          {storedSummaries.map((summaryItem) => (
            <div key={summaryItem.id} className="mb-4 border rounded-lg overflow-hidden shadow-sm">
              <div
                className="flex justify-between items-center p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                onClick={() => toggleSummary(summaryItem.id)}
              >
                <div className="flex-1 overflow-hidden">
                  <p className="font-medium text-gray-800 truncate" title={summaryItem.articleLink}>
                    {summaryItem.articleLink || "Summary"}
                  </p>
                  <p className="text-sm text-gray-500">
                    {new Date(summaryItem.timestamp.seconds * 1000).toLocaleString()}
                  </p>
                </div>
                {expandedSummary === summaryItem.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
              {expandedSummary === summaryItem.id && (
                <div className="p-4 bg-white border-t border-gray-200">
                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-700 mb-1">Link</h5>
                    <a href={summaryItem.articleLink} target="_blank" rel="noopener noreferrer" className="text-[rgb(100,204,201)] hover:underline break-all">
                      {summaryItem.articleLink}
                    </a>
                  </div>
                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-700 mb-1">Summary</h5>
                    <div className="whitespace-pre-wrap text-gray-700 p-3 bg-gray-50 rounded-md text-sm">{summaryItem.summary}</div>
                  </div>
                  {summaryItem.articleText && (
                    <div>
                      <h5 className="font-semibold text-gray-700 mb-1">Original Article Text</h5>
                      <div className="whitespace-pre-wrap text-gray-600 p-3 bg-gray-50 rounded-md max-h-48 overflow-y-auto text-sm">
                        {summaryItem.articleText}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PildorasSonneil; 